import type { Accessor } from '../function';
import { isAccessor, isFunction } from '../function';
import type { ValueOf } from '../object';
import { extend, isNotNullOrUndefined } from '../object';
import type { Optional } from '../object/object-types/optional';
import { caseInsensitiveIncludes, isString } from '../string';
import { EMPTY_BATCH_ITERATION_DATA } from './array-consts';
import type { ItemOf, ItemOrItems, NonEmptyArray } from './array-types';
import type { BatchIterationData } from './array-types/batch-iteration-data';

export function isEmptyArray<Items extends unknown[]>(array: Optional<Items> | []): array is null | undefined | [] {
  return !array?.length;
}

export function isNotEmptyArray<Item>(array: Optional<Item[]>): array is NonEmptyArray<Item> {
  return !isEmptyArray(array);
}

export function isArray<Item>(array: Item[] | unknown): array is Item[] {
  return Array.isArray(array);
}

export function isNotArray<Item>(array: Item[] | unknown): array is unknown {
  return !isArray(array);
}

export function replaceInIndex<Item>(array: Item[], index: number, newItem: Item) {
  return clonedArray(array).map((originalItem, i) => (i !== index ? originalItem : newItem));
}

export function extendInIndex<Item extends {}>(array: Item[], index: number, extension: Partial<Item>): Item[] {
  return clonedArray(array).map((originalItem, i) => (i !== index ? originalItem : extend(originalItem, extension)));
}

export function clonedArray<Item>(array: Item[]) {
  return [...array];
}

export function removeInIndex<Item>(array: Optional<Item[]>, index: number) {
  return isNotEmptyArray(array) ? clonedArray(array).filter((_, i) => i !== index) : [];
}

export function purge<Item>(array: (Optional<Item>)[]) {
  return array.filter<Item>(isNotNullOrUndefined);
}

export function purgeFlat<Item>(array: (Optional<Item[]>)[]) {
  const flattened = array.flat();

  return purge(flattened);
}

export function isMultiple<Items extends unknown[]>(array: Items | undefined): array is Items {
  return isNotEmptyArray(array) && isLengthGreaterThan(array, 1);
}

export function isSingleItem<Items extends unknown[]>(array: Optional<Items> | [ItemOf<Items>]): array is [ItemOf<Items>] {
  return isLengthOf(array, 1);
}

export function isLengthOf<Items extends unknown[]>(array: Optional<Items>, length: number): array is Items {
  return isNotEmptyArray(array) && array.length === length;
}

export function isLengthGreaterThan(array: unknown[], length: number) {
  return array.length > length;
}

export function sortDescending<Item extends Object>(items: Item[], accessor: Accessor<Item, number | Date>): Item[];
export function sortDescending<Item extends Object>(items: Item[], key: keyof Item): Item[];
export function sortDescending<Item extends Object>(items: Item[], keyOrAccessor: keyof Item | Accessor<Item, number | Date>): Item[] {
  if (isAccessor<Item, number | Date>(keyOrAccessor)) {
    return sort(items, keyOrAccessor).reverse();
  }

  return sort(items, keyOrAccessor).reverse();
}

export function purgeMap<Item extends Object, Key extends keyof Item>(items: Optional<Item[]>, key: Key): NonNullable<Item[Key]>[];
export function purgeMap<Item extends Object, Value>(items: Optional<Item[]>, key: Accessor<Item, Value>): NonNullable<Value[]>;
export function purgeMap<Item extends Object>(items: Optional<Item[]>, keyOrAccessor: Parameters<typeof extract>[1]) {
  const extractedItems = extract(items, keyOrAccessor);

  return purge(extractedItems);
}

export function extract<Item extends Object, Key extends keyof Item>(items: Optional<Item[]>, key: Key): Item[Key][];
export function extract<Item extends Object, Value>(items: Optional<Item[]>, key: Accessor<Item, Value>): Value[];
export function extract<Item extends Object, Value>(items: Optional<Item[]>, keyOrAccessor: Accessor<Item, Value> | keyof Item) {
  if (isEmptyArray(items)) {
    return [];
  }
  
  const accessor = isFunction(keyOrAccessor) 
    ? keyOrAccessor
    : (item: Item) => item[keyOrAccessor];

  return items.map(item => accessor(item));
}

export function uniqueExtract<Item extends Object, Key extends keyof Item>(items: Optional<Item[]>, key: Key): Item[Key][];
export function uniqueExtract<Item extends Object, Value>(items: Optional<Item[]>, key: Accessor<Item, Value>): Value[];
export function uniqueExtract<Item extends Object>(items: Optional<Item[]>, keyOrAccessor: Parameters<typeof extract>[1]) {
  const extractedItems = extract(items, keyOrAccessor);

  return uniqueObjects(extractedItems);
}

export function flatExtract<Item extends Object, Key extends keyof Item>(items: Optional<Item[]>, key: Key): Item[Key];
export function flatExtract<Item extends Object, Value>(items: Optional<Item[]>, key: Accessor<Item, Value>): Value;
export function flatExtract<Item extends Object>(items: Optional<Item[]>, keyOrAccessor: Parameters<typeof extract>[1]) {
  const extractedItems = extract(items, keyOrAccessor);

  return extractedItems.flat();
}

export function last<Items extends unknown[]>(items: Items): ItemOf<Items> {
  return isEmptyArray(items) ? undefined : items[items.length - 1];
}

export function filter<Item>(items: Item[], value: string, accessor: Accessor<Item, string>): Item[];
export function filter<Item>(items: Item[], value: string, key: keyof Item): Item[];
export function filter<Item>(
  items: Item[],
  value: string,
  accessorOrKey: Accessor<Item, string> | keyof Item,
): Item[] {
  const accessor = isFunction(accessorOrKey)
    ? accessorOrKey
    : (item: Item) => `${item[accessorOrKey]}`;

  return items.filter(item => caseInsensitiveIncludes(accessor(item), value));
}

export function sort<Item extends Object>(items: Item[], accessor: Accessor<Item, number | Date>): Item[];
export function sort<Item extends Object>(items: Item[], key: keyof Item): Item[]; 
export function sort<Item extends Object>(items: Item[], keyOrAccessor: keyof Item | Accessor<Item, number | Date>): Item[] {
  const accessor = isFunction(keyOrAccessor) 
    ? keyOrAccessor
    : (item: Item) => item[keyOrAccessor];

  return items.sort((a, b) => accessor(a) > accessor(b) ? 1 : -1);
}

export function intersect<Item>(array1: Item[], array2: Item[]) {
  return array1.filter(item => array2.includes(item));
}

export function outersect<Item>(array1: Item[], array2: Item[]) {
  return array1.filter(item => !array2.includes(item));
}

export function outersectBy<Item extends Object, Key extends keyof Item>(array1: Item[], array2: Item[], key: Key): Item[];
export function outersectBy<Item extends Object, Value>(array1: Item[], array2: Item[], accessor: Accessor<Item, Value>): Item[];
export function outersectBy<Item extends Object, Value>(array1: Item[], array2: Item[], accessorOrKey: Accessor<Item, Value> | keyof Item) {
  const accessor = isFunction(accessorOrKey)
    ? accessorOrKey
    : (item: Item) => item[accessorOrKey];

  return array1.filter(item => !array2.some(item2 => accessor(item) === accessor(item2)));
}

export function makeMultiple<Item>(itemOrItems: ItemOrItems<Item>) {
  return Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
}

export function uniqueBy<Item extends Object, Key extends keyof Item>(array: Item[], key: Key): Item[];
export function uniqueBy<Item extends Object, Value>(array: Item[], accessor: Accessor<Item, Value>): Item[];
export function uniqueBy<Item extends Object, Value>(array: Item[], accessorOrKey: Accessor<Item, Value> | keyof Item): Item[] {
  const accessor = isFunction(accessorOrKey)
    ? accessorOrKey
    : (item: Item) => item[accessorOrKey];

  return array.filter((item, item1Index) => array.findIndex(item2 => accessor(item) === accessor(item2)) === item1Index);
}

export function findRequired<Item>(array: Item[], predicate: (item: Item, index: number, obj: Item[]) => boolean, arrayName?: string): Item {
  const foundItem = array.find(predicate);

  if (!foundItem) {
    throw new Error(`No item found in ${arrayName ?? 'array'}`);
  }

  return foundItem;
}

export function uniqueObjects<Item>(array: Item[]): Item[] {
  const seenObjectsJson = new Set();

  return array.filter(item => {
    const json = JSON.stringify(item);
    const isNew = !seenObjectsJson.has(json);

    if (isNew) {
      seenObjectsJson.add(json);
    }

    return isNew;
  });  
}

export function newArrayOfLength(length: number) {
  return Array.from({ length });
}

export function validateNotEmptyArray<Item>(array: Optional<Item[]>, arrayName?: string): asserts array is NonEmptyArray<Item> {
  if (isEmptyArray(array)) {
    throw new Error(`${arrayName ?? 'array'} is empty`);
  }
}

export function validateArray<Item>(array: Item[] | unknown, arrayName?: string): asserts array is Item[] {
  if (!isArray(array)) {
    throw new Error(`${arrayName ?? 'array'} is not an array`);
  }
}

export function batch<Item>(items: Item[], batchSize: number) {
  return newArrayOfLength(
    Math.ceil(items.length / batchSize)).map(
    (_, batchIndex) => items.slice(
      batchIndex * batchSize,
      (batchIndex + 1) * batchSize,
    ),
  );
}

export function batchByMaxPayloadSize<Item>(items: Item[], maxPayloadSize: number, toPayload: (item: Item) => string) {
  const sizeLimitedBatches = items.reduce<BatchIterationData<Item>[]>((batchIterationData, item) => {
    const payload = toPayload(item);
    const payloadSize = Buffer.byteLength(payload, 'utf8');
    const currentBatch = last(batchIterationData);

    if (currentBatch.currentBatchSize + payloadSize > maxPayloadSize) {
      const newBatch: BatchIterationData<Item> = {
        currentBatchSize: payloadSize,
        items: [item],
      };

      return [...batchIterationData, newBatch];
    }

    const completedBatches = batchIterationData.slice(0, -1);

    const updatedCurrentBatch: BatchIterationData<Item> = {
      currentBatchSize: currentBatch.currentBatchSize + payloadSize,
      items: [...currentBatch.items, item],
    };

    return [...completedBatches, updatedCurrentBatch];
  }, [EMPTY_BATCH_ITERATION_DATA]);

  return extract(sizeLimitedBatches, 'items');
}

export function filterBy<Item>(array: Item[], query: string, ...fields: (keyof Item)[]) {
  const filterByField = (item: Item, field: keyof Item) => {
    const fieldValue = item[field];

    return (isString(fieldValue) && caseInsensitiveIncludes(fieldValue, query));
  };
  
  return array.filter((item) => fields.some(
    (field) => filterByField(item, field)),
  );
}

export function isNotIn<Item>(array: Optional<Item[]>, ...items: Item[]) {
  return isEmptyArray(array) || array.every(item => !items.includes(item));
}

export function every<Item>(array: Optional<Item[]>, predicate: (item: Item) => boolean) {
  return isNotEmptyArray(array) && array.every(predicate);
}

export function append<Item>(array: Item[], value: Item): Item[] {
  return [...array, value];
}

export function remove<Item>(array: Item[], value: Item): Item[] {
  return array.filter((item) => item !== value);
}

export function removeBy<Item>(array: Item[], predicate: (item: Item) => boolean): Item[] {
  return array.filter((item) => !predicate(item));
}

export function removeByField<Item, Field extends keyof Item>(array: Item[], value: ValueOf<Item>, field: Field): Item[] {
  return removeBy(array, (item) => item[field] === value);
}

export function takeFromStart<Item>(array: Item[], amount: number): Item[] {
  return array.slice(0, amount);
}

export function takeFromEnd<Item>(array: Item[], amount: number): Item[] {
  return array.slice(-amount);
}

export function take<Item>(array: Item[], startIndex: number, endIndex: number): Item[] {
  return array.slice(startIndex, endIndex);
}

export function fillArrayOfLength<Item>(length: number, item: Item): Item[] {
  return Array.from({ length }).map(() => item);
}

export function validateIsArray(value: any, name: string): asserts value is any[] {
  if (isNotArray(value)) {
    throw new Error(`${name} is not an array`);
  }
}

export function validateIsSingleItem<Item>(array: Optional<Item[]>, name: string): asserts array is [Item] {
  if (!isSingleItem(array)) {
    throw new Error(`${name} is not a single item`);
  }
}

export function whenNotEmptyArray<Item, Result>(
  operationOnArray: (array: Item[]) => Result,
  array: Item[] | undefined,
) {
  return isEmptyArray(array) ? undefined : operationOnArray(array);
}

export function extractIds<Item extends { id: string }>(
  items: Optional<Item[]>,
): string[] {
  return extract(items, 'id');
}